import React, { useEffect, useState } from 'react';
import { getConfig } from '../services/api';

function Section({ title, children }) {
  return (
    <div className="card" style={{ padding: '14px 16px', marginBottom: 12 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'var(--text-muted)', marginBottom: 10, borderBottom: '1px solid var(--border)', paddingBottom: 6,
      }}>
        {title}
      </div>
      <div className="kv-grid" style={{ fontSize: 13 }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, value, mono, badge, badgeClass }) {
  if (value === null || value === undefined) return null;
  const displayVal = typeof value === 'boolean'
    ? (value ? 'true' : 'false')
    : String(value);

  const boolBadge = typeof value === 'boolean';
  const badgeCls  = badgeClass || (boolBadge ? (value ? 'completed' : 'failed') : 'processing');

  return (
    <div className="kv-row">
      <span className="kv-label" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</span>
      <span className="kv-value" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {badge || boolBadge ? (
          <span className={`badge ${badgeCls}`} style={{ fontSize: 10 }}>{displayVal}</span>
        ) : (
          <span className={mono ? 'mono' : ''} style={{ fontWeight: 600, fontSize: 13 }}>{displayVal}</span>
        )}
      </span>
    </div>
  );
}

function Null() {
  return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>;
}

export default function Config() {
  const [cfg, setCfg]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getConfig()
      .then(setCfg)
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="processing-indicator" style={{ marginTop: 60 }}>
        <div className="spinner" />
        <span>Loading configuration…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div className="error-box" style={{ display: 'inline-block', padding: '12px 20px', fontSize: 13 }}>
          Failed to load config: {error}
        </div>
      </div>
    );
  }

  const { environment, llm, embedding, matching, scoring, processor, sharing } = cfg;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '16px 20px' }}>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Runtime Configuration</h2>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>
          Read-only view of active environment settings. No secrets are shown.
        </p>
      </div>

      {/* API Docs */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: 12 }}>
        <div style={{
          fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
          color: 'var(--text-muted)', marginBottom: 10, borderBottom: '1px solid var(--border)', paddingBottom: 6,
        }}>
          API Documentation
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Interactive Swagger UI — try every endpoint live with JWT auth.
          </span>
          <button
            className="btn btn-primary"
            style={{ fontSize: 12, padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => window.open('/api-docs', '_blank')}
          >
            Open API Docs ↗
          </button>
        </div>
      </div>

      {/* Environment */}
      <Section title="Environment">
        <Row label="Dev Mode"  value={environment.dev_mode}  badge />
        <Row label="Log Level" value={environment.log_level} mono />
      </Section>

      {/* LLM Backend */}
      <Section title="LLM Backend">
        <Row label="Mode" value={llm.use_api_gateway ? 'API Gateway (LiteLLM)' : 'Direct Vertex AI'} badgeClass="llm-mode" badge />
        <Row label="Active Model" value={llm.active_model} mono />
        {llm.gateway && <>
          <Row label="Gateway Base URL"    value={llm.gateway.base_url}          mono />
          <Row label="Gateway Model"       value={llm.gateway.model}             mono />
          <Row label="Max Context Tokens"  value={llm.gateway.max_context_tokens} mono />
        </>}
        {llm.vertex && <>
          <Row label="GCP Project" value={llm.vertex.project} mono />
          <Row label="GCP Region"  value={llm.vertex.region}  mono />
          <Row label="Vertex Model" value={llm.vertex.model}  mono />
        </>}
      </Section>

      {/* Embedding */}
      <Section title="Embedding">
        <Row label="Mode"          value={embedding.use_api_gateway ? 'API Gateway' : 'Vertex AI'} badgeClass="vector-mode" badge />
        <Row label="Active Model"  value={embedding.active_model}  mono />
        <Row label="Dimensions"    value={embedding.dimensions}    mono />
      </Section>

      {/* Matching Pipeline */}
      <Section title="Matching Pipeline">
        <Row label="Default Matching Mode"           value={matching.default_mode}     badgeClass={matching.default_mode === 'llm' ? 'llm-mode' : matching.default_mode === 'vector' ? 'vector-mode' : 'hybrid-mode'} badge />
        <Row label="Default Top-N Results"           value={matching.top_n_default}    mono />
        <Row label="Expenditure Batch Size (LLM)"    value={matching.exp_batch_size}   mono />
        <Row label="Expenditure Concurrency"         value={matching.exp_concurrency}  mono />
        <Row label="Candidate Chunk Size"            value={matching.chunk_size}       mono />
        <Row label="Chunk Concurrency"               value={matching.chunk_concurrency} mono />
      </Section>

      {/* Scoring Weights */}
      <Section title="Scoring Weights">
        <Row label="Name Exact Match"            value={scoring.name_exact_match}           mono />
        <Row label="Name + Purpose Match"        value={scoring.name_purpose_match}         mono />
        <Row label="Name + Purpose + Program"    value={scoring.name_purpose_program_match} mono />
        <Row label="Purpose Match"               value={scoring.purpose_match}              mono />
        <Row label="Program Match"               value={scoring.program_match}              mono />
        <Row label="Name Present, No Match"      value={scoring.name_present_no_match}      mono />
        <Row label="Exclusion Penalty"           value={scoring.exclusion_penalty}          mono />
        <Row label="Restriction Bonus"           value={scoring.restriction_bonus}          mono />
        <Row label="Age Max Bonus"               value={scoring.age_max_bonus}              mono />
        <Row label="Age Years for Max Bonus"     value={scoring.age_years_for_max}          mono />
        <Row label="Purpose Overlap Threshold"   value={scoring.purpose_overlap_threshold}  mono />
        <Row label="Program Overlap Threshold"   value={scoring.program_overlap_threshold}  mono />
      </Section>

      {/* System / Processor */}
      <Section title="System">
        <Row label="Processor Poll Interval"    value={`${processor.poll_interval_ms} ms`}   mono />
        <Row label="Stuck Request Timeout"      value={`${(processor.stuck_timeout_ms / 60000).toFixed(0)} min (${processor.stuck_timeout_ms} ms)`} mono />
        {processor.dev_max_expenditures != null && (
          <Row label="Dev Max Expenditures (dev only)" value={processor.dev_max_expenditures} mono />
        )}
        <Row label="Share Public" value={sharing.share_public} badge />
      </Section>

    </div>
  );
}
